import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Heart } from "lucide-react";
import LocalizedLink from "@/components/LocalizedLink";
import ArticleCard from "@/components/ArticleCard";
import { useLanguage, langPrefix } from "@/i18n/LanguageContext";
import { useUserAuth } from "@/user/UserAuthContext";
import { useFavorites } from "@/favorites/FavoritesContext";
import { useTools, useArticles } from "@/content/ContentProvider";
import { AUTH_STRINGS } from "@/user/authStrings";

/**
 * Authenticated-user account page.
 *
 * Surfaces:
 *   1. Identity card (avatar + name + email + auth method)
 *   2. Saved Items panel — Tools / Articles tabs, hydrated from
 *      `/api/favorites` and joined against the live ContentProvider
 *      catalogues so the cards render exactly as on /tools and
 *      /articles.
 */
export default function Account() {
  const { lang } = useLanguage();
  const L = AUTH_STRINGS[lang];
  const { user, logout } = useUserAuth();
  const navigate = useNavigate();

  if (!user) {
    // UserProtectedRoute guarantees we should not get here; keep a
    // graceful fallback for the brief instant before redirect.
    return null;
  }

  const method =
    user.has_password && user.has_google
      ? L.methodBoth
      : user.has_google
      ? L.methodGoogle
      : L.methodPassword;

  const displayInitial = (user.name || user.email || "?")
    .slice(0, 1)
    .toUpperCase();

  const onLogout = async () => {
    // Navigate BEFORE clearing the session so the re-render of
    // UserProtectedRoute (reacting to `status=unauthenticated`) doesn't
    // race our navigate() and send us to /auth/login instead of home.
    navigate(langPrefix(lang) || "/", { replace: true });
    await logout();
  };

  return (
    <section
      className="max-w-3xl mx-auto px-5 sm:px-6 py-12"
      data-testid="account-page"
    >
      <div className="sas-eyebrow mb-2">{L.eyebrow}</div>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="sas-h1" data-testid="account-title">
            {L.accountTitle}
          </h1>
          <p className="sas-body mt-3 text-[15px]">{L.accountBody}</p>
        </div>
        <button
          type="button"
          onClick={onLogout}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-[color:var(--sas-ink)] px-3 py-1.5 rounded-full border border-[color:var(--sas-line)] hover:border-[color:var(--sas-ink)] transition-colors shrink-0"
          data-testid="account-logout"
        >
          <LogOut size={14} aria-hidden />
          {L.logout}
        </button>
      </div>

      <div
        className="mt-8 p-5 rounded-2xl border border-[color:var(--sas-line)] bg-white"
        data-testid="account-summary"
      >
        <div className="flex items-center gap-4">
          {user.picture ? (
            <img
              src={user.picture}
              alt=""
              aria-hidden
              className="w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center text-[color:var(--sas-accent)] text-base font-semibold"
              style={{ background: "var(--sas-accent-soft)" }}
              aria-hidden
            >
              {displayInitial}
            </div>
          )}
          <div className="min-w-0">
            <div
              className="text-[16px] font-semibold text-[color:var(--sas-ink)] truncate"
              data-testid="account-name"
            >
              {user.name}
            </div>
            <div
              className="text-[13px] text-[color:var(--sas-ink-2)] truncate"
              data-testid="account-email"
              dir="ltr"
            >
              {user.email}
            </div>
          </div>
        </div>

        <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-[13px]">
          <div className="flex items-center justify-between gap-3 py-2 border-t border-[color:var(--sas-line)]">
            <dt className="text-[color:var(--sas-ink-2)]">{L.accountEmail}</dt>
            <dd className="text-[color:var(--sas-ink)] truncate" dir="ltr">
              {user.email}
            </dd>
          </div>
          <div className="flex items-center justify-between gap-3 py-2 border-t border-[color:var(--sas-line)]">
            <dt className="text-[color:var(--sas-ink-2)]">{L.accountMethod}</dt>
            <dd
              className="text-[color:var(--sas-ink)]"
              data-testid="account-method"
            >
              {method}
            </dd>
          </div>
        </dl>
      </div>

      <div className="mt-10">
        <SavedItemsSection lang={lang} L={L} />
      </div>
    </section>
  );
}

/**
 * "Saved Items" panel. Two tabs (Tools / Articles) with empty states
 * and a CTA to browse the catalogue. Hydrates by joining the
 * favourites list (slugs) against the live ContentProvider catalogues
 * so cards render exactly the same as on /tools and /articles.
 */
function SavedItemsSection({ lang, L }) {
  const { fetchList, counts } = useFavorites();
  const { TOOLS: catalogTools = [] } = useTools();
  const { ARTICLES = [] } = useArticles();
  const [favorites, setFavorites] = useState({ tools: [], articles: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("tools");

  // Re-fetch the saved-list whenever a favourite is toggled anywhere
  // on the site. We listen to a string derived from the live counts
  // so the list invalidates without manual wiring.
  const trigger = `${counts.tool}-${counts.article}`;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const data = await fetchList();
      if (!cancelled) {
        setFavorites(data || { tools: [], articles: [] });
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  // Hydrated lists — sorted by `saved_at` desc (server already sorts).
  // Anything that no longer exists in the catalogue (e.g. admin removed
  // a tool after the user saved it) is silently dropped.
  const savedTools = useMemo(() => {
    const bySlug = new Map(catalogTools.map((t) => [t.slug, t]));
    return favorites.tools
      .map((f) => bySlug.get(f.item_slug))
      .filter(Boolean);
  }, [favorites, catalogTools]);

  const savedArticles = useMemo(() => {
    const bySlug = new Map(ARTICLES.map((a) => [a.slug, a]));
    return favorites.articles
      .map((f) => bySlug.get(f.item_slug))
      .filter(Boolean);
  }, [favorites, ARTICLES]);

  return (
    <section data-testid="account-saved-items">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Heart
              size={18}
              aria-hidden
              className="text-[color:var(--sas-accent)]"
              fill="currentColor"
              strokeWidth={0}
            />
            <h2 className="text-[20px] sm:text-[22px] font-semibold text-[color:var(--sas-ink)]">
              {L.savedItemsTitle}
            </h2>
          </div>
          <p className="sas-body mt-1 text-[14px]">{L.savedItemsBody}</p>
        </div>
      </div>

      {/* Tabs */}
      <div
        className="mt-5 inline-flex items-center gap-1 p-1 rounded-full border border-[color:var(--sas-line)] bg-white"
        role="tablist"
        aria-label={L.savedItemsTitle}
      >
        <SavedTab
          active={activeTab === "tools"}
          onClick={() => setActiveTab("tools")}
          label={L.savedItemsTabTools}
          count={savedTools.length}
          testId="account-saved-tab-tools"
        />
        <SavedTab
          active={activeTab === "articles"}
          onClick={() => setActiveTab("articles")}
          label={L.savedItemsTabArticles}
          count={savedArticles.length}
          testId="account-saved-tab-articles"
        />
      </div>

      <div className="mt-6">
        {activeTab === "tools" ? (
          <SavedToolsPanel
            tools={savedTools}
            loading={loading}
            lang={lang}
            L={L}
          />
        ) : (
          <SavedArticlesPanel
            articles={savedArticles}
            loading={loading}
            L={L}
          />
        )}
      </div>
    </section>
  );
}

function SavedTab({ active, onClick, label, count, testId }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testId}
      className={`
        inline-flex items-center gap-1.5
        px-3.5 h-8 rounded-full text-[13px] font-medium
        transition-colors
        ${
          active
            ? "bg-[color:var(--sas-accent)] text-white"
            : "text-[color:var(--sas-ink-2)] hover:text-[color:var(--sas-ink)]"
        }
      `}
    >
      <span>{label}</span>
      <span
        className={`
          inline-flex items-center justify-center min-w-[20px] h-5 px-1.5
          rounded-full text-[11px] font-semibold
          ${
            active
              ? "bg-white/20 text-white"
              : "bg-[color:var(--sas-accent-soft)] text-[color:var(--sas-accent)]"
          }
        `}
      >
        {count}
      </span>
    </button>
  );
}

function SavedToolsPanel({ tools, loading, lang, L }) {
  if (loading) {
    return (
      <div
        className="text-[13px] text-[color:var(--sas-ink-2)] py-6"
        data-testid="account-saved-tools-loading"
      >
        {L.savedItemsLoading}
      </div>
    );
  }
  if (!tools.length) {
    return <SavedEmptyState L={L} cta="tools" />;
  }
  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="account-saved-tools">
      {tools.map((t) => (
        <article
          key={t.slug}
          className="sas-card flex flex-col"
          data-testid={`account-saved-tool-${t.slug}`}
        >
          <h3 className="text-[16px] font-semibold text-[color:var(--sas-ink)]">
            {t.name}
          </h3>
          <p className="sas-body mt-2 text-[13.5px] line-clamp-2">
            {(t.desc && (t.desc[lang] || t.desc.en)) || ""}
          </p>
          <div className="mt-4">
            <a
              href={t.url}
              target="_blank"
              rel="noopener noreferrer"
              className="sas-tool-cta"
            >
              {lang === "ar" ? "جرّب الأداة" : "Try Tool"}
            </a>
          </div>
        </article>
      ))}
    </div>
  );
}

function SavedArticlesPanel({ articles, loading, L }) {
  if (loading) {
    return (
      <div
        className="text-[13px] text-[color:var(--sas-ink-2)] py-6"
        data-testid="account-saved-articles-loading"
      >
        {L.savedItemsLoading}
      </div>
    );
  }
  if (!articles.length) {
    return <SavedEmptyState L={L} cta="articles" />;
  }
  return (
    <div
      className="grid sm:grid-cols-2 gap-6"
      data-testid="account-saved-articles"
    >
      {articles.map((a) => (
        <ArticleCard key={a.slug} article={a} compact />
      ))}
    </div>
  );
}

function SavedEmptyState({ L, cta }) {
  const ctaLabel =
    cta === "articles" ? L.savedItemsBrowseArticles : L.savedItemsBrowseTools;
  const ctaTo = cta === "articles" ? "/articles" : "/tools";
  return (
    <div
      className="rounded-2xl border border-dashed border-[color:var(--sas-line)] bg-white px-6 py-10 text-center"
      data-testid="account-saved-empty"
    >
      <div
        className="mx-auto w-12 h-12 rounded-full flex items-center justify-center"
        style={{ background: "var(--sas-accent-soft)", color: "var(--sas-accent)" }}
        aria-hidden
      >
        <Heart size={20} />
      </div>
      <p className="mt-4 text-[14px] text-[color:var(--sas-ink-2)]">
        {L.savedItemsEmpty}
      </p>
      <LocalizedLink
        to={ctaTo}
        className="sas-btn-link mt-4 inline-flex"
        data-testid="account-saved-empty-cta"
      >
        {ctaLabel}
      </LocalizedLink>
    </div>
  );
}
